import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useIsSm } from '@/lib/hooks/useMediaQuery.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useNewIssueDialogStore } from '@/routes/projectStages/issues/lib/stores/useNewIssueDialogStore.ts'

// Gate: visible on mobile, on the per-issueType list route (not the detail),
// when a currIssueType is known and the create dialog isn't already open.
// The create dialog host lives inside Issues.tsx, so this FAB only works on
// routes where that host is mounted — deliberately narrower than the old
// IssuesMobileFooter it replaces. Extending to archive/dashboard requires
// hoisting the dialog first.
const ISSUES_LIST_RE = /\/issue_types\/[^/]+\/issues(?:\/?)(\?.*)?$/

export default function MobileFab() {
  const isMobile = useIsSm()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const currIssueType = useProjectStageStore(s => s.currIssueType)
  const dialogOpen = useNewIssueDialogStore(s => s.dialogOpen)
  const setDialogOpen = useNewIssueDialogStore(s => s.updateDialogState)
  const setDraftIssueId = useNewIssueDialogStore(s => s.setDraftIssueId)

  if (!isMobile) return null
  if (!currIssueType?.currentUserCanCreate) return null
  if (!ISSUES_LIST_RE.test(pathname)) return null
  if (dialogOpen) return null

  const openNewIssue = () => {
    // Clear any stale draftIssueId from a previous draft-edit session.
    // Without this, useGetDraftIssueData would refetch that issue and
    // prepopulate the "new issue" form with its title / attachments.
    setDraftIssueId(null)
    setDialogOpen(true)
  }

  return (
    <button
      type="button"
      onClick={openNewIssue}
      aria-label={t('project.issues.newIssue')}
      data-cy="mobile-fab-new-issue"
      style={{
        backgroundColor: currIssueType.color,
        bottom: `calc(var(--bottomTabBarHeight) + var(--safe-area-inset-bottom) + 16px)`,
      }}
      className={`fixed right-4 z-[90] flex size-14 items-center justify-center rounded-full text-white shadow-lg transition-transform active:scale-95 md:hidden`}
    >
      <Plus className={`size-6`} strokeWidth={2.5} />
    </button>
  )
}
